import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, Optional } from '@angular/core';
import { AvanoButtonBarButtonDetails, AvanoButtonBarGroup, AvanoLoadingIndicatorService } from '@avano/avano-components';
import { Ability } from '@casl/ability';
import { ProductInternalResponseDto } from '@scip/api';
import { ProductControllerService } from '@scip/api';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { translationKeys } from '../../../core';
import { AbilityActions, AbilitySites, BackendErrorService, ProductsService, ProductTabIdentifier, ProductTabsService, UploadService } from '../../../services';
import { ValidationErrorsService } from '@avano/avano-components/avano-validation/validation-errors-service';

@Component({
  selector: 'scip-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  @Input() set productId(val: ProductTabIdentifier) {
    if (val && val !== this._productId) {
      this._productId = val;
      this.loadingIdentifier = 'blockedTabContent' + this._productId;
    }
  }

  @Output() readonly cancelRequested = new EventEmitter<void>();

  @Output() readonly saveTriggered = new EventEmitter<ProductTabIdentifier>();

  @Output() readonly uploadDossierTriggered = new EventEmitter<string>();

  items: AvanoButtonBarButtonDetails[];
  showDossierUploadDialog = false;
  loadingIdentifier: string;
  product$: Observable<ProductInternalResponseDto>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private saveBtn: AvanoButtonBarButtonDetails;
  private cancelCloseBtn: AvanoButtonBarButtonDetails;
  private uploadProductBtn: AvanoButtonBarButtonDetails;
  private validationErrorsButton: AvanoButtonBarButtonDetails;

  private unsavedOneTime = false;

  private _productId: ProductTabIdentifier;

  constructor(
    private ability: Ability,
    private productControllerService: ProductControllerService,
    private productTabsService: ProductTabsService,
    private uploadService: UploadService,
    private productsService: ProductsService,
    private backendErrorService: BackendErrorService,
    private changeDet: ChangeDetectorRef,
    @Optional() private validationService: ValidationErrorsService,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService
  ) {}

  ngOnInit(): void {
    this.avanoLoadingIndicatorService.block([this.loadingIdentifier]);
    this.initializeButtonBar();
    this.productsService
      .getUserEditedKeysAsArray()
      .pipe(
        map((allUnsavedTabs: string[]) => allUnsavedTabs.includes(this._productId)),
        takeUntil(this.destroyed$)
      )
      .subscribe((isDirtyTab: boolean) => {
        if (isDirtyTab && !this.unsavedOneTime) {
          this.items.push(this.saveBtn);
          this.items.push(this.validationErrorsButton);
          this.items = [...this.items];
          this.cancelCloseBtn.label = translationKeys.commonTranslationKey.btn.cancel;
          this.uploadProductBtn.isDisabled = true;
          this.unsavedOneTime = true;
        }
        if (!isDirtyTab) {
          this.items.splice(2, 2);
          this.items = [...this.items];
          this.unsavedOneTime = false;
          this.cancelCloseBtn.label = translationKeys.commonTranslationKey.btn.close;
          this.uploadProductBtn.isDisabled = false;
        }
        this.changeDet.markForCheck();
      });
    this.product$ = this.productControllerService.getProductById(+this._productId).pipe(
      tap(({ name, id }: ProductInternalResponseDto) => this.productTabsService.setTabName(`${id}`, name)),
      catchError((err) => {
        this.backendErrorService.handleError(err, translationKeys.productDetailsComponent.msg.error.loadingProduct);
        this.cancelRequested.emit();
        return of(null);
      }),
      shareReplay(1),
      tap(() => this.avanoLoadingIndicatorService.unblock([this.loadingIdentifier]))
    );
    this.validationService?.changed$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (errors) => {
        this.validationErrorsButton.errorDetails = errors;
        this.saveBtn.isCallToActionButton = !(errors?.length > 0);
        this.saveBtn.isDisabled = errors?.length > 0;
      }
    });
  }

  closeDossierUploadDialog(isDossierUploaded: boolean): void {
    if (isDossierUploaded) {
      this.uploadDossierTriggered.emit(this._productId);
      this.getUpdatedProduct(this._productId);
    }
    this.showDossierUploadDialog = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private getUpdatedProduct(id: string): void {
    this.productControllerService.getProductById(+id).subscribe((data) => this.uploadService.productIsUploaded(data));
  }

  private initializeButtonBar(): void {
    this.saveBtn = {
      icon: 'icon-content-save',
      label: translationKeys.commonTranslationKey.btn.save,
      action: () => this.saveTriggered.emit(this._productId),
      isDisabled: false,
      isCallToActionButton: true,
      group: AvanoButtonBarGroup.PRIMARY
    };

    this.validationErrorsButton = {
      icon: null,
      label: null,
      errorDetails: [],
      group: AvanoButtonBarGroup.PRIMARY
    };

    this.cancelCloseBtn = {
      icon: 'icon-content-cancel',
      label: translationKeys.commonTranslationKey.btn.close,
      action: () => this.cancelRequested.emit(),
      group: AvanoButtonBarGroup.PRIMARY
    };

    this.items = [this.cancelCloseBtn];

    if (this.ability.can(AbilityActions.update, AbilitySites.submission)) {
      this.uploadProductBtn = {
        icon: 'icon-content-upload',
        label: translationKeys.productDetailsComponent.typescript.uploadDossierButtonText,
        action: () => this.openSubmitDossierDialog(),
        group: AvanoButtonBarGroup.SECONDARY
      };
      this.items.push(this.uploadProductBtn);
    }
  }

  private openSubmitDossierDialog() {
    this.showDossierUploadDialog = true;
  }
}
